'use client';

import { useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Map, ArrowRight, X } from 'lucide-react';
import type { Country } from '@/hooks/useCountries';

interface QuickCompareToolProps {
  countries: Country[];
}

export default function QuickCompareTool({ countries }: QuickCompareToolProps) {
  const router = useRouter();
  const [selectedCountries, setSelectedCountries] = useState<Country[]>([]);
  const [searchQuery1, setSearchQuery1] = useState('');
  const [searchQuery2, setSearchQuery2] = useState('');

  // 鎼滅储鍥藉
  const searchCountries = useCallback((query: string) => {
    if (!query.trim()) return [];
    
    const lowercaseQuery = query.toLowerCase();
    return countries.filter(country => 
      country.name.toLowerCase().includes(lowercaseQuery) ||
      country.iso3.toLowerCase().includes(lowercaseQuery)
    ).slice(0, 5); // 闄愬埗鏄剧ず5涓粨鏋?
  }, [countries]);

  const handleCountrySelect = (country: Country, index: number) => {
    setSelectedCountries(prev => {
      const newSelection = [...prev];
      newSelection[index] = country;
      return newSelection;
    });
    
    // 娓呯┖瀵瑰簲鐨勬悳绱㈡
    if (index === 0) {
      setSearchQuery1('');
    } else {
      setSearchQuery2('');
    }
  };

  const handleRemoveCountry = (index: number) => {
    setSelectedCountries(prev => {
      const newSelection = [...prev];
      newSelection[index] = undefined as any;
      return newSelection;
    });
  };

  const handleCompare = () => {
    if (selectedCountries[0] && selectedCountries[1]) {
      const url = `/map?a=${selectedCountries[0].slug}&b=${selectedCountries[1].slug}`;
      router.push(url);
    }
  };

  const canCompare = selectedCountries[0] && selectedCountries[1];

  return (
    <div className="bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Map className="h-4 w-4 text-sky-600" />
        <h2 className="text-base font-semibold text-slate-900">Quick Compare Tool</h2>
      </div>
      
      <p className="text-slate-600 mb-4 text-sm">
        Select two countries to compare their true sizes on the interactive map.
      </p>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {/* 绗竴涓浗瀹堕€夋嫨 */}
        <div className="relative">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            First Country
          </label>
          
          {selectedCountries[0] ? (
            <div className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg">
              <img
                src={selectedCountries[0].flag}
                alt={`${selectedCountries[0].name} flag`}
                width={32}
                height={24}
                className="w-8 h-6 object-cover rounded border border-slate-200"
                loading="lazy"
              />
              <span className="flex-1 font-medium text-slate-900">
                {selectedCountries[0].name}
              </span>
              <button
                onClick={() => handleRemoveCountry(0)}
                className="p-1 hover:bg-slate-100 rounded"
                aria-label="Remove first country"
              >
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                type="text"
                value={searchQuery1}
                onChange={(e) => setSearchQuery1(e.target.value)}
                placeholder="Search for a country..."
                className="w-full p-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              />
              
              {searchQuery1 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10">
                  {searchCountries(searchQuery1).map((country) => (
                    <button
                      key={country.iso3}
                      onClick={() => handleCountrySelect(country, 0)}
                      className="w-full p-3 text-left hover:bg-slate-50 flex items-center gap-3"
                    >
                      <img
                        src={country.flag}
                        alt={`${country.name} flag`}
                        width={24}
                        height={16}
                        className="w-6 h-4 object-cover rounded border border-slate-200"
                        loading="lazy"
                      />
                      <span className="font-medium text-slate-900">{country.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 绗簩涓浗瀹堕€夋嫨 */}
        <div className="relative">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Second Country
          </label>
          
          {selectedCountries[1] ? (
            <div className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg">
              <img
                src={selectedCountries[1].flag}
                alt={`${selectedCountries[1].name} flag`}
                width={32}
                height={24}
                className="w-8 h-6 object-cover rounded border border-slate-200"
                loading="lazy"
              />
              <span className="flex-1 font-medium text-slate-900">
                {selectedCountries[1].name}
              </span>
              <button
                onClick={() => handleRemoveCountry(1)}
                className="p-1 hover:bg-slate-100 rounded"
                aria-label="Remove second country"
              >
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                type="text"
                value={searchQuery2}
                onChange={(e) => setSearchQuery2(e.target.value)}
                placeholder="Search for a country..."
                className="w-full p-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              />
              
              {searchQuery2 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10">
                  {searchCountries(searchQuery2).map((country) => (
                    <button
                      key={country.iso3}
                      onClick={() => handleCountrySelect(country, 1)}
                      className="w-full p-3 text-left hover:bg-slate-50 flex items-center gap-3"
                    >
                      <img
                        src={country.flag}
                        alt={`${country.name} flag`}
                        width={24}
                        height={16}
                        className="w-6 h-4 object-cover rounded border border-slate-200"
                        loading="lazy"
                      />
                      <span className="font-medium text-slate-900">{country.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 姣旇緝鎸夐挳 */}
      <div className="flex justify-center">
        <button
          onClick={handleCompare}
          disabled={!canCompare}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
            canCompare
              ? 'bg-sky-700 text-white hover:bg-sky-800'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
        >
          <Map className="h-4 w-4" />
          Compare on Map
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

